/**
 * ChatMessages Component
 * 
 * Displays the list of chat messages with auto-scroll functionality.
 * Includes bot messages, user messages, loading indicator, and HITL approval.
 */

import { useRef, useEffect } from 'react';
import BotMessageBubbleChatbot from '@/components/botMessageBubbleChatbot';
import UserMessageBubbleChatbot from '@/components/userMessageBubbleChatbot';
import LoadingMessageBubbleChatbot from '@/components/loadingMessageBubbleChatbot';
import HitlApprovalMessage from '@/components/HitlApprovalMessage';
import { useChatStore } from '@/store/chatStore';
import type { ChatMode } from './ChatHeader';

export interface Message {
  id: string;
  content: string;
  from: 'user' | 'bot';
  audio?: string | null;
  timestamp?: Date;
  isStreaming?: boolean;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  mode: ChatMode;
  onHitlDecision?: (decision: 'approve' | 'reject', threadId: string, message?: string) => void;
}

export function ChatMessages({ messages, isLoading, mode, onHitlDecision }: ChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { pendingHitlInterrupt, isWaitingForHitlDecision } = useChatStore();

  // Auto-scroll to bottom when user sends a message or HITL interrupt appears
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.from === 'user' || isWaitingForHitlDecision) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isWaitingForHitlDecision]);

  const handleHitlDecision = (decision: 'approve' | 'reject', message?: string) => {
    if (pendingHitlInterrupt && onHitlDecision) {
      onHitlDecision(decision, pendingHitlInterrupt.threadId, message);
    }
  };

  return (
    <div className="flex flex-col space-y-3">
      {messages.map((message) => {
        const isBot = message.from === 'bot';
        return isBot ? (
          <BotMessageBubbleChatbot
            key={message.id}
            id={message.id}
            content={message.content}
            audio={message.audio}
            timestamp={message.timestamp}
            isStreaming={message.isStreaming}
          />
        ) : (
          <UserMessageBubbleChatbot
            key={message.id}
            content={message.content}
            audio={message.audio}
            timestamp={message.timestamp}
          />
        );
      })}

      {/* HITL Approval Message */}
      {isWaitingForHitlDecision && pendingHitlInterrupt && (
        <HitlApprovalMessage
          interrupt={pendingHitlInterrupt}
          onDecision={handleHitlDecision}
        />
      )}

      {/* Loading Indicator */}
      {isLoading && !isWaitingForHitlDecision && (
        <LoadingMessageBubbleChatbot mode={mode} />
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}

